import { Link } from "react-router-dom";
import { Mail, MessageCircle, Clock, ShieldCheck, Send, RefreshCcw } from "lucide-react";
import Logo from "./Logo";

function FooterCol({ title, children }) {
  return (
    <div>
      <h4
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--text-subtle)",
          margin: 0,
          marginBottom: 14,
        }}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}

function FooterLink({ to, href, children, ...rest }) {
  const style = {
    display: "block",
    color: "var(--text-muted)",
    textDecoration: "none",
    fontSize: 14,
    padding: "5px 0",
    transition: "color .15s ease",
  };
  const onHover = (e) => (e.currentTarget.style.color = "var(--text)");
  const onLeave = (e) => (e.currentTarget.style.color = "var(--text-muted)");

  if (href) {
    return (
      <a href={href} style={style} onMouseEnter={onHover} onMouseLeave={onLeave} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} style={style} onMouseEnter={onHover} onMouseLeave={onLeave} {...rest}>
      {children}
    </Link>
  );
}

function TrustItem({ icon: Icon, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        fontSize: 13.5,
        color: "var(--text-muted)",
        padding: "5px 0",
        lineHeight: 1.45,
      }}
    >
      <Icon size={14} color="var(--primary)" style={{ marginTop: 3, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      style={{
        marginTop: 64,
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <div
        className="gd-page"
        style={{ padding: "56px 24px 32px" }}
      >
        <div className="gd-footer-grid">
          {/* Column 1 — Brand */}
          <div data-testid="footer-col-brand">
            <Logo size="md" />
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: 13,
                lineHeight: 1.6,
                margin: "18px 0 14px",
                maxWidth: 280,
              }}
            >
              Temporary flight reservations with a live PNR - for visa applications and onward
              travel planning.
            </p>
            <div style={{ fontSize: 12.5, color: "var(--text-subtle)", lineHeight: 1.6 }}>
              Operated by <strong style={{ color: "var(--text-muted)", fontWeight: 600 }}>Get Departo</strong>
              <br />
              Bengaluru, India
            </div>
          </div>

          {/* Column 2 — Links */}
          <FooterCol title="Links">
            <FooterLink to="/about" data-testid="footer-link-about">About Us</FooterLink>
            <FooterLink to="/terms" data-testid="footer-link-terms">Terms</FooterLink>
            <FooterLink to="/privacy" data-testid="footer-link-privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms#refund" data-testid="footer-link-refund">Refund Policy</FooterLink>
            <FooterLink to="/about#contact" data-testid="footer-link-contact">Contact</FooterLink>
          </FooterCol>

          {/* Column 3 — Support */}
          <FooterCol title="Support">
            <FooterLink href="mailto:support@getdeparto.com" data-testid="footer-email">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Mail size={13} color="var(--text-subtle)" />
                support@getdeparto.com
              </span>
            </FooterLink>
            <FooterLink
              href="https://wa.me/919036705707"
              data-testid="footer-whatsapp"
              target="_blank"
              rel="noreferrer"
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <MessageCircle size={13} color="var(--text-subtle)" />
                WhatsApp support
              </span>
            </FooterLink>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13.5,
                color: "var(--text-muted)",
                padding: "5px 0",
              }}
            >
              <Clock size={13} color="var(--text-subtle)" />
              <span>24 × 7 response support</span>
            </div>
          </FooterCol>

          {/* Column 4 — Trust */}
          <FooterCol title="Trust">
            <TrustItem icon={ShieldCheck}>Secure payments via Razorpay</TrustItem>
            <TrustItem icon={Send}>Delivered via WhatsApp + email</TrustItem>
            <TrustItem icon={RefreshCcw}>Reissue or refund support</TrustItem>
          </FooterCol>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            marginTop: 48,
            paddingTop: 22,
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12.5,
            color: "var(--text-subtle)",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span data-testid="footer-copyright">
            © 2026 Get Departo • All rights reserved
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 99,
                background: "var(--success)",
                display: "inline-block",
              }}
            />
            All systems operational
          </span>
        </div>
      </div>
    </footer>
  );
}
